import { useEffect, useState, useCallback } from "react";
import { useModuleVisibility } from "../hooks/useModuleVisibility";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { PageHeader } from "../components/ui-helpers";
import Cip007Patch from "./Cip007Patch";
import Cip010Baselines from "./Cip010Baselines";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowRight, ClockCountdown, CheckCircle, Circle } from "@phosphor-icons/react";

const CLOCK = {
  overdue: "bg-red-100 text-red-700 border-red-300",
  due_soon: "bg-amber-100 text-amber-800 border-amber-300",
  current: "bg-green-100 text-green-700 border-green-300",
  none: "bg-slate-100 text-slate-500 border-slate-300",
};
const clockLabel = (c) => {
  if (!c || c.status === "none") return "—";
  if (c.status === "overdue") return `Overdue ${Math.abs(c.days_left)}d`;
  if (c.status === "due_soon") return `Due in ${c.days_left}d`;
  return c.due ? `Due ${c.due}` : "On track";
};
const Clock = ({ c, testid }) => (
  <span data-testid={testid} className={`text-xs px-2 py-0.5 rounded border ${CLOCK[c?.status || "none"]}`}>{clockLabel(c)}</span>
);
const STAGES = [
  { key: "evaluation", label: "Evaluate", hint: "Identify security patches (R2.2)" },
  { key: "patch_or_mitigate", label: "Patch / Mitigate", hint: "35 days (R2.3)" },
  { key: "baseline", label: "New Baseline", hint: "30 days (R1.3)" },
  { key: "complete", label: "Complete", hint: "Post-change baseline captured" },
];
const stageIdx = (s) => STAGES.findIndex((x) => x.key === s);

export default function ClaroPatch() {
  const { isRole } = useAuth();
  const canEdit = isRole("admin", "analyst");
  const [tab, setTab] = useState("lifecycle");
  const { isSubHidden } = useModuleVisibility();
  const ptabs = [["lifecycle", "Lifecycle"], ["patches", "Patch Evaluation (R2)"], ["baselines", "Baselines (R1)"]]
    .filter(([v]) => !isSubHidden(`claropatch:${v}`));
  useEffect(() => {
    if (isSubHidden(`claropatch:${tab}`)) setTab(ptabs[0]?.[0] || "lifecycle");
  }, [tab, ptabs, isSubHidden]);

  return (
    <div data-testid="claropatch-page">
      <PageHeader title="ClaroPatch" subtitle="Change-driven patch & baseline lifecycle — evaluate → patch/mitigate (35d) → new baseline (30d)" />
      <div className="px-8 pt-6">
        <div className="inline-flex border border-border">
          {ptabs.map(([v, l], i) => (
            <button key={v} data-testid={`claropatch-tab-${v}`} onClick={() => setTab(v)}
              className={`px-4 py-1.5 text-xs font-semibold ${tab === v ? "bg-primary text-primary-foreground" : "hover:bg-secondary"} ${i > 0 ? "border-l border-border" : ""}`}>{l}</button>
          ))}
        </div>
      </div>
      {tab === "lifecycle" && <Lifecycle canEdit={canEdit} />}
      {tab === "patches" && <div className="-mt-2"><Cip007Patch embedded /></div>}
      {tab === "baselines" && <div className="-mt-2"><Cip010Baselines embedded /></div>}
    </div>
  );
}

function Lifecycle({ canEdit }) {
  const [data, setData] = useState({ changes: [], counts: {}, overdue: 0 });
  const [advance, setAdvance] = useState(null); // {change}
  const [postSnaps, setPostSnaps] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/claropatch/lifecycle"); setData(data); }
    catch (e) { /* bcsi-gated handled below via empty */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdvance = async (c) => {
    setAdvance({ change: c, patch_eval_date: c.patch_eval_date || "", change_completed_date: c.change_completed_date || "", post_baseline_link_id: "" });
    try {
      const { data } = await api.get("/cip010/baselines");
      const list = Array.isArray(data) ? data : (data.baselines || data.snapshots || []);
      setPostSnaps(list.filter((s) => s.snapshot_type === "post_change"));
    } catch (e) { setPostSnaps([]); }
  };
  const submitAdvance = async (patch) => {
    setSaving(true);
    try {
      await api.post(`/claropatch/lifecycle/${advance.change.id}/advance`, patch);
      toast.success("Lifecycle updated"); setAdvance(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-8 space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {STAGES.map((s) => (
          <div key={s.key} className="border border-border bg-card p-4" data-testid={`claropatch-count-${s.key}`}>
            <div className="overline text-muted-foreground text-xs">{s.label}</div>
            <div className="text-2xl font-bold mt-1">{data.counts?.[s.key] ?? 0}</div>
          </div>
        ))}
        <div className="border border-border bg-card p-4">
          <div className="overline text-muted-foreground text-xs">Overdue</div>
          <div data-testid="claropatch-count-overdue" className={`text-2xl font-bold mt-1 ${data.overdue > 0 ? "text-red-600" : ""}`}>{data.overdue}</div>
        </div>
      </div>

      <div className="flex items-start gap-2 border border-border bg-secondary/40 px-4 py-3 text-sm">
        <ClockCountdown size={18} className="mt-0.5 shrink-0 text-muted-foreground" />
        <p className="text-muted-foreground">Each CIP change ticket flows through the lifecycle: evaluate for applicable security patches, then a <b>35-day</b> window to patch or create a mitigation plan (CIP-007 R2.3), then a <b>30-day</b> window to capture the updated Post-Change baseline (CIP-010 R1.3). Start a change from ClaroFlow → "CIP Templates" → <i>CIP-010 R1 Baseline Change Management</i>.</p>
      </div>

      <div className="border border-border bg-card">
        <div className="border-b border-border px-4 py-3 font-display font-bold text-sm">Change lifecycle</div>
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border bg-secondary/40 text-left overline text-muted-foreground">
            <th className="px-4 py-2">Change Ticket</th><th className="px-4 py-2">Stage</th><th className="px-4 py-2">Patch/Mitigate (35d)</th><th className="px-4 py-2">Baseline (30d)</th><th className="px-4 py-2">Baselines</th><th className="px-4 py-2"></th>
          </tr></thead>
          <tbody>
            {data.changes.map((c) => (
              <tr key={c.id} data-testid={`claropatch-row-${c.id}`} className="border-b border-border align-top">
                <td className="px-4 py-3"><div className="font-medium">{c.title}</div><div className="text-xs text-muted-foreground">{c.assignee || "unassigned"} · {c.status}</div></td>
                <td className="px-4 py-3"><StageBar stage={c.stage} /></td>
                <td className="px-4 py-3"><Clock c={c.patch_clock} testid={`claropatch-patchclock-${c.id}`} /><div className="text-[11px] text-muted-foreground mt-0.5">{c.patch_eval_date ? `eval ${c.patch_eval_date}` : "not evaluated"}</div></td>
                <td className="px-4 py-3"><Clock c={c.baseline_clock} testid={`claropatch-blclock-${c.id}`} /><div className="text-[11px] text-muted-foreground mt-0.5">{c.change_completed_date ? `changed ${c.change_completed_date}` : "not deployed"}</div></td>
                <td className="px-4 py-3 text-xs">
                  <div>Pre: {c.pre_baseline ? <span className="text-green-700">{c.pre_baseline.snapshot_type}</span> : <span className="text-muted-foreground">none</span>}</div>
                  <div>Post: {c.post_baseline ? <span className="text-green-700">✓</span> : <span className="text-muted-foreground">none</span>}</div>
                </td>
                <td className="px-4 py-3">{canEdit && c.stage !== "complete" && <Button size="sm" variant="outline" className="rounded-none gap-1" data-testid={`claropatch-advance-${c.id}`} onClick={() => openAdvance(c)}>Advance <ArrowRight size={13} /></Button>}</td>
              </tr>
            ))}
            {data.changes.length === 0 && <tr><td colSpan={6} className="px-4 py-4 text-muted-foreground text-sm">No change tickets yet. Create one from ClaroFlow using the "CIP Templates" → CIP-010 R1 Baseline Change Management template.</td></tr>}
          </tbody>
        </table>
      </div>

      {advance && (
        <Dialog open onOpenChange={(o) => !o && setAdvance(null)}>
          <DialogContent className="rounded-none max-w-md" data-testid="claropatch-advance-dialog">
            <DialogHeader><DialogTitle>Advance — {advance.change.title}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>1 · Patch evaluation completed (R2.2) — starts the 35-day clock</Label>
                <Input data-testid="claropatch-adv-evaldate" type="date" value={advance.patch_eval_date} onChange={(e) => setAdvance({ ...advance, patch_eval_date: e.target.value })} className="rounded-none mt-1" /></div>
              <div><Label>2 · Change deployed — starts the 30-day baseline clock (R1.3)</Label>
                <Input data-testid="claropatch-adv-changedate" type="date" value={advance.change_completed_date} onChange={(e) => setAdvance({ ...advance, change_completed_date: e.target.value })} className="rounded-none mt-1" /></div>
              <div><Label>3 · Link Post-Change baseline (completes the change)</Label>
                <Select value={advance.post_baseline_link_id || "none"} onValueChange={(v) => setAdvance({ ...advance, post_baseline_link_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="claropatch-adv-postbl" className="rounded-none mt-1"><SelectValue placeholder="Select post-change baseline" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— none —</SelectItem>
                    {postSnaps.map((s) => <SelectItem key={s.id} value={s.id}>{s.asset_name} · {s.snapshot_timestamp?.slice(0, 10)}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1">Capture the Post-Change baseline on the Baselines tab first, then link it here.</p>
              </div>
              <Button data-testid="claropatch-adv-save" disabled={saving} className="w-full rounded-none bg-primary hover:bg-primary/90"
                onClick={() => submitAdvance({
                  patch_eval_date: advance.patch_eval_date || null,
                  change_completed_date: advance.change_completed_date || null,
                  post_baseline_link_id: advance.post_baseline_link_id || null,
                })}>Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function StageBar({ stage }) {
  const idx = stageIdx(stage);
  return (
    <div className="flex items-center gap-1" data-testid={`claropatch-stage-${stage}`}>
      {STAGES.map((s, i) => (
        <div key={s.key} className="flex items-center gap-1" title={`${s.label} — ${s.hint}`}>
          {i <= idx ? <CheckCircle size={16} weight="fill" className={stage === "complete" && i === idx ? "text-green-600" : "text-primary"} /> : <Circle size={16} className="text-muted-foreground/40" />}
          {i < STAGES.length - 1 && <div className={`h-px w-3 ${i < idx ? "bg-primary" : "bg-border"}`} />}
        </div>
      ))}
      <span className="text-xs ml-1 font-medium">{STAGES[idx]?.label}</span>
    </div>
  );
}
